import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Mail, Copy, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const CODE_RE = /^[A-Z0-9\-]{4,64}$/;

export default function Redeem() {
  const { code: paramCode } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(paramCode ? "email" : "code");
  const [code, setCode] = useState((paramCode || "").toUpperCase());
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null); // { code, products, status, is_expired, expires_at }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { keys: [...], email, products }

  // Prefill from URL param
  useEffect(() => {
    if (paramCode) {
      const c = paramCode.toUpperCase();
      setCode(c);
      lookup(c);
    }
  }, [paramCode]);

  const lookup = async (rawCode) => {
    const c = (rawCode || code).trim().toUpperCase();
    if (!c) return toast.error("Zadejte kód");
    if (!CODE_RE.test(c)) return toast.error("Kód má neplatný formát");
    setPreviewLoading(true);
    setPreview(null);
    try {
      const r = await api.get(`/redeem/${encodeURIComponent(c)}`);
      setPreview(r.data);
      if (r.data.status === "redeemed") {
        toast.error("Tento poukaz už byl uplatněn");
      } else if (r.data.status === "revoked") {
        toast.error("Tento poukaz byl zneplatněn");
      } else if (r.data.is_expired) {
        toast.error("Platnost poukazu vypršela");
      } else {
        setStep("email");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kód nenalezen";
      toast.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    if (!email || !email.includes("@")) return toast.error("Zadejte platný e-mail");
    setSubmitting(true);
    try {
      const r = await api.post("/redeem", { code: code.trim().toUpperCase(), email: email.trim().toLowerCase() });
      setResult(r.data);
      setStep("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uplatnění selhalo");
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = (k) => {
    navigator.clipboard?.writeText(k);
    toast.success("Klíč zkopírován");
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("cs-CZ"); } catch (e) { return iso.slice(0, 10); }
  };

  return (
    <div className="pt-10 pb-20 min-h-[70vh]" data-testid="redeem-page">
      <SEO title="Uplatnit poukaz" description="Zadejte svůj poukazový kód a získejte licenční klíč zakoupený mimo náš e-shop." noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-widest mb-4">
            <Ticket className="w-3.5 h-3.5"/>Uplatnění poukazu
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Máte kód z Aukra?</h1>
          <p className="text-zinc-600 max-w-lg mx-auto">Zadejte svůj jedinečný poukazový kód a e-mail – během chvíle vám doručíme licenční klíč přímo do schránky.</p>
        </div>

        {step === "code" && (
          <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8" data-testid="redeem-step-code">
            <label className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Poukazový kód</label>
            <div className="mt-2 flex gap-2">
              <input
                autoFocus
                className="flex-1 px-4 py-3.5 rounded-2xl border border-zinc-200 bg-white text-lg font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="SS-XXXX-XXXX-XXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
                data-testid="redeem-code-input"
                maxLength={64}
              />
              <button
                onClick={() => lookup()}
                disabled={previewLoading || !code}
                className="btn-primary px-6 py-3.5 rounded-2xl text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="redeem-lookup-btn"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
              </button>
            </div>
            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5"/> Kód najdete v komentáři nebo zprávě od prodejce.
            </div>
          </div>
        )}

        {step === "email" && preview && (
          <div className="space-y-4" data-testid="redeem-step-email">
            <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8">
              <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700 flex items-center gap-1.5 mb-3">
                <CheckCircle2 className="w-4 h-4"/> Kód je platný
              </div>
              <div className="font-mono font-bold text-2xl mb-1" data-testid="redeem-preview-code">{preview.code}</div>
              <div className="text-xs text-zinc-500 mb-5">Platnost do {fmtDate(preview.expires_at)}</div>

              <div className="border-t border-zinc-100 pt-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-3">Získáte licenci pro</div>
                <div className="space-y-2">
                  {(preview.products || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50 border border-zinc-100" data-testid={`redeem-preview-product-${p.slug}`}>
                      {(p.images && p.images[0]) ? (
                        <img src={p.images[0]} alt="" className="w-12 h-12 rounded-xl object-cover"/>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"><Sparkles className="w-5 h-5 text-blue-500"/></div>
                      )}
                      <div className="font-semibold text-sm">{p.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8">
              <label className="text-xs uppercase tracking-widest font-semibold text-zinc-500 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5"/> Váš e-mail
              </label>
              <input
                type="email"
                autoFocus
                className="mt-2 w-full px-4 py-3.5 rounded-2xl border border-zinc-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="jmeno@email.cz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                data-testid="redeem-email-input"
              />
              <div className="mt-2 text-xs text-zinc-500">Klíč Vám pošleme sem – ihned po potvrzení.</div>

              <button
                onClick={submit}
                disabled={submitting || !email}
                className="btn-primary w-full mt-5 py-3.5 rounded-2xl text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="redeem-submit-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin"/> Zpracovávám…</> : <>Získat klíč <ArrowRight className="w-4 h-4"/></>}
              </button>
              <button
                onClick={() => { setStep("code"); setPreview(null); }}
                className="w-full mt-2 text-xs text-zinc-500 hover:text-zinc-900 py-2"
                data-testid="redeem-back-btn"
              >
                ← Zadat jiný kód
              </button>
            </div>
          </div>
        )}

        {step === "email" && !preview && previewLoading && (
          <div className="text-center py-10 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline-block"/></div>
        )}

        {step === "done" && result && (
          <div className="space-y-4" data-testid="redeem-step-done">
            <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-8 text-center">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3"/>
              <h2 className="font-display font-bold text-2xl text-emerald-900 mb-1">Hotovo!</h2>
              <p className="text-sm text-emerald-800">Klíče jsme poslali na <strong>{result.email}</strong>. Můžete je také zkopírovat níže.</p>
            </div>

            <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-4">Vaše licenční klíče</div>
              <div className="space-y-3">
                {(result.keys || []).map((k, idx) => (
                  <div key={idx} className="border border-zinc-200 rounded-2xl p-4" data-testid={`redeem-result-key-${idx}`}>
                    <div className="text-xs text-zinc-500 mb-2">{k.product_name}</div>
                    <div className="flex items-center gap-2 bg-zinc-900 text-emerald-300 font-mono text-sm rounded-xl px-4 py-3 select-all break-all">
                      <span className="flex-1">{k.key}</span>
                      <button onClick={() => copyKey(k.key)} className="text-zinc-300 hover:text-white shrink-0" title="Kopírovat" data-testid={`redeem-copy-key-${idx}`}>
                        <Copy className="w-4 h-4"/>
                      </button>
                    </div>
                    {(k.download_url || k.activation_url) && (
                      <div className="flex flex-wrap gap-4 mt-3 text-xs">
                        {k.download_url && <a href={k.download_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Stáhnout software →</a>}
                        {k.activation_url && <a href={k.activation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Návod na aktivaci →</a>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center pt-4">
              <button onClick={() => navigate("/")} className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="redeem-home-btn">
                Zpět na Správný Směr
              </button>
            </div>
          </div>
        )}

        {preview && (preview.is_expired || preview.status === "revoked" || preview.status === "redeemed") && (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 mt-4 flex gap-3" data-testid="redeem-invalid">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"/>
            <div className="text-sm text-amber-900">
              {preview.status === "redeemed" && "Tento poukaz byl už uplatněn. Klíč byl doručen na e-mail zadaný při uplatnění."}
              {preview.status === "revoked" && "Tento poukaz byl zneplatněn. Kontaktujte prodejce."}
              {preview.is_expired && preview.status !== "redeemed" && preview.status !== "revoked" && "Platnost tohoto poukazu vypršela. Kontaktujte prodejce."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
